/** SASE API (SASE-1). The section's module data is read through the modules
 *  it mounts (Private access, Agent gateways), never copied (SD-01). */
import { http } from "@/lib/http";
import type { AuthenticatedUser } from "@/stores/authStore";

import type {
  AiAppDetail,
  AiAppFilters,
  AiAppSummary,
  AiCatalogInfo,
  AiCategory,
  AiChecks,
  AiChecksOut,
  AiOverview,
  CorporateIdentifiers,
  CorporateIdentifiersOut,
  McpServersOut,
  Page,
  PatternCheckOut,
  PromptProtection,
  PromptProtectionOut,
  Sanction,
  SanctionOut,
} from "./ai/types";
import type {
  ActivityFilters,
  ActivitySearchResponse,
  AppView,
  TopField,
  TopMetric,
  TopResponse,
  UserAccessEvents,
  UserView,
  Window,
} from "./analyticsTypes";
import type { ProductView, ProductViewUpdate } from "./types";

/** The signed-in account, with its product view. */
export async function getAccount(): Promise<AuthenticatedUser> {
  const { data } = await http.get<AuthenticatedUser>("/auth/me");
  return data;
}

/** Set which sections the console shows this account (SD-01). */
export async function setProductView(view: ProductView): Promise<AuthenticatedUser> {
  const body: ProductViewUpdate = { product_view: view };
  const { data } = await http.put<AuthenticatedUser>("/auth/me/product-view", body);
  return data;
}

// ---- Analytics (SASE-14): the organisation's own activity log --------------

/** The query string of a log read. `kind` repeats (`kind=web&kind=dns`), which
 *  is what the API reads; axios would otherwise send `kind[]=`. */
export function logParams(
  filters: ActivityFilters,
  window: Window,
  extra: Record<string, string | number | undefined> = {},
): URLSearchParams {
  const params = new URLSearchParams();
  for (const kind of filters.kinds ?? []) params.append("kind", kind);
  for (const id of filters.event_ids ?? []) params.append("event_id", id);
  const single: (keyof Omit<ActivityFilters, "kinds" | "event_ids">)[] = [
    "user",
    "device",
    "app",
    "category",
    "action",
    "rule_id",
    "pop",
    "txn_id",
    "severity",
    "threat_category",
    "web_category",
    "ai_app",
    "ai_category",
    "casb_app",
    "casb_activity",
    "mcp_server",
    "q",
  ];
  for (const key of single) {
    const value = filters[key];
    if (value) params.set(key, value);
  }
  if (window.since) params.set("since", window.since);
  if (window.until) params.set("until", window.until);
  for (const [key, value] of Object.entries(extra)) {
    if (value !== undefined && value !== "") params.set(key, String(value));
  }
  return params;
}

/** Every request, lookup, flow and alert of the organisation, newest first. */
export async function searchActivity(
  filters: ActivityFilters,
  window: Window,
  cursor?: string,
  limit = 100,
): Promise<ActivitySearchResponse> {
  const { data } = await http.get<ActivitySearchResponse>("/sase/logs/search", {
    params: logParams(filters, window, { limit, cursor }),
  });
  return data;
}

// ---- AI security (SASE-13): /sase/ai… -----------------------------------------
// Every call is the caller's own organisation's: the tenant is the session's.

/** The catalog's version and where it comes from. */
export async function getAiCatalog(): Promise<AiCatalogInfo> {
  const { data } = await http.get<AiCatalogInfo>("/sase/ai/catalog");
  return data;
}

export async function getAiOverview(): Promise<AiOverview> {
  const { data } = await http.get<AiOverview>("/sase/ai/overview");
  return data;
}

export async function listAiApps(filters: AiAppFilters): Promise<Page<AiAppSummary>> {
  const { data } = await http.get<Page<AiAppSummary>>("/sase/ai/apps", { params: filters });
  return data;
}

export async function listAiCategories(): Promise<AiCategory[]> {
  const { data } = await http.get<AiCategory[]>("/sase/ai/apps/categories");
  return data;
}

export async function getAiApp(appId: string): Promise<AiAppDetail> {
  const { data } = await http.get<AiAppDetail>(`/sase/ai/apps/${encodeURIComponent(appId)}`);
  return data;
}

/** Allow, block, corporate only or read only. */
export async function setAiSanction(appId: string, control: Sanction): Promise<SanctionOut> {
  const { data } = await http.put<SanctionOut>(
    `/sase/ai/apps/${encodeURIComponent(appId)}/sanction`,
    { control },
  );
  return data;
}

/** Back to not set. */
export async function clearAiSanction(appId: string): Promise<void> {
  await http.delete(`/sase/ai/apps/${encodeURIComponent(appId)}/sanction`);
}

export async function listAiSanctions(): Promise<Page<SanctionOut>> {
  const { data } = await http.get<Page<SanctionOut>>("/sase/ai/sanctions", {
    params: { page_size: 100 },
  });
  return data;
}

/** The same search as CSV (one page, up to 1000 lines). */
export async function exportActivity(filters: ActivityFilters, window: Window): Promise<Blob> {
  const { data } = await http.get<Blob>("/sase/logs/export", {
    params: logParams(filters, window),
    responseType: "blob",
  });
  return data;
}

/** The most frequent (or heaviest) values of one field. */
export async function topValues(
  field: TopField,
  filters: ActivityFilters,
  window: Window,
  size = 5,
  metric: TopMetric = "count",
): Promise<TopResponse> {
  const { data } = await http.get<TopResponse>("/sase/logs/top", {
    params: logParams(filters, window, { field, metric, size }),
  });
  return data;
}

/** One user's timeline, counts by kind and decision, and top sites. */
export async function getUserView(
  user: string,
  window: Window,
  cursor?: string,
  limit = 100,
): Promise<UserView> {
  const { data } = await http.get<UserView>(`/sase/logs/users/${encodeURIComponent(user)}`, {
    params: logParams({}, window, { limit, cursor }),
  });
  return data;
}

/** One user's private access events in the window (sign-ins, sessions, app
 *  access and the device checks behind each decision), read-only. */
export async function getUserAccessEvents(
  user: string,
  window: Window,
  limit = 200,
): Promise<UserAccessEvents> {
  const { data } = await http.get<UserAccessEvents>(
    `/sase/logs/users/${encodeURIComponent(user)}/access`,
    { params: logParams({}, window, { limit }) },
  );
  return data;
}

/** Everything done with one app, with counts and its top users. */
export async function getAppView(
  app: string,
  window: Window,
  cursor?: string,
  limit = 100,
): Promise<AppView> {
  const { data } = await http.get<AppView>(`/sase/logs/apps/${encodeURIComponent(app)}`, {
    params: logParams({}, window, { limit, cursor }),
  });
  return data;
}

/** SASE → Policies (SASE-7): the API's paths. They live in this file because
 *  the FE↔BE parity guard reads each feature folder's own `api.ts`; the client
 *  that calls them is `policies/api.ts`. */
export const policyPaths = {
  policies: () => "/sase/policies",
  policy: (id: string) => `/sase/policies/${id}`,
  rules: (id: string) => `/sase/policies/${id}/rules`,
  rule: (id: string, ruleId: string) => `/sase/policies/${id}/rules/${ruleId}`,
  moveRule: (id: string, ruleId: string) => `/sase/policies/${id}/rules/${ruleId}/move`,
  /** C11: a settings change and several rule writes, all or nothing. */
  batch: (id: string) => `/sase/policies/${id}/batch`,
  objects: (id: string) => `/sase/policies/${id}/objects`,
  object: (id: string, objectId: string) => `/sase/policies/${id}/objects/${objectId}`,
  profiles: (id: string) => `/sase/policies/${id}/profiles`,
  profile: (id: string, profileId: string) => `/sase/policies/${id}/profiles/${profileId}`,
  publish: (id: string) => `/sase/policies/${id}/publish`,
  versions: (id: string) => `/sase/policies/${id}/versions`,
  version: (id: string, n: number) => `/sase/policies/${id}/versions/${n}`,
  versionDiff: (id: string, n: number) => `/sase/policies/${id}/versions/${n}/diff`,
  rollback: (id: string, n: number) => `/sase/policies/${id}/versions/${n}/rollback`,
  diff: (id: string) => `/sase/policies/${id}/diff`,
  compiled: (id: string) => `/sase/policies/${id}/compiled`,
  test: (id: string) => `/sase/policies/${id}/test`,
};

export async function getAiCorporate(): Promise<CorporateIdentifiersOut> {
  const { data } = await http.get<CorporateIdentifiersOut>("/sase/ai/corporate");
  return data;
}

export async function setAiCorporate(body: CorporateIdentifiers): Promise<CorporateIdentifiersOut> {
  const { data } = await http.put<CorporateIdentifiersOut>("/sase/ai/corporate", body);
  return data;
}

export async function getPromptProtection(): Promise<PromptProtectionOut> {
  const { data } = await http.get<PromptProtectionOut>("/sase/ai/prompt-protection");
  return data;
}

export async function setPromptProtection(body: PromptProtection): Promise<PromptProtectionOut> {
  const { data } = await http.put<PromptProtectionOut>("/sase/ai/prompt-protection", body);
  return data;
}

/** Whether a security node would take this custom pattern (nothing is saved). */
export async function checkPromptPattern(pattern: string): Promise<PatternCheckOut> {
  const { data } = await http.post<PatternCheckOut>("/sase/ai/prompt-protection/check-pattern", {
    pattern,
  });
  return data;
}

// ---- SASE-34 (B20): MCP servers, conversation checks, injection alerts -----

export async function getAiChecks(): Promise<AiChecksOut> {
  const { data } = await http.get<AiChecksOut>("/sase/ai/checks");
  return data;
}

export async function setAiChecks(body: AiChecks): Promise<AiChecksOut> {
  const { data } = await http.put<AiChecksOut>("/sase/ai/checks", body);
  return data;
}

/** The MCP servers seen in the organisation's traffic (default: the last 30 days). */
export async function listMcpServers(
  params: { since?: string; page?: number; page_size?: number } = {},
): Promise<McpServersOut> {
  const { data } = await http.get<McpServersOut>("/sase/ai/mcp-servers", { params });
  return data;
}
